import React from 'react';
import Modal from 'react-modal';

import CBMediaQuery from '../shared/CBMediaQuery';
import { getModalStyle, getStyle } from '../../utils/utils';
import ScaniaApiCredentialsHelp, { HELP_ID } from './ScaniaApiCredentialsHelp';
import { scaniaConnectionStyles as styles, modalStyle } from './scaniaConnectionStyles';

function looksLikeEmail(value) {
  const text = (value || '').trim();
  if (!text || text.indexOf(' ') !== -1) {
    return false;
  }
  const at = text.indexOf('@');
  if (at < 1) {
    return false;
  }
  const domain = text.slice(at + 1);
  const dot = domain.indexOf('.');
  return domain.indexOf('@') === -1 && dot > 0 && dot < domain.length - 1;
}

const emptyForm = {
  label: '',
  clientId: '',
  secret: '',
  showSecret: false,
  helpOpen: false,
};

class ScaniaConnectionDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = { ...emptyForm };
    this.helpTrigger = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onDocumentKeyDown, true);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen) {
      this.setState({ ...emptyForm });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onDocumentKeyDown, true);
  }

  onDocumentKeyDown = (event) => {
    if (!this.props.isOpen) {
      return;
    }
    if (event.key !== 'Escape' && event.keyCode !== 27) {
      return;
    }
    if (this.state.helpOpen) {
      event.stopPropagation();
      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }
      event.preventDefault();
      this.closeHelp();
    }
  };

  closeHelp = () => {
    this.setState({ helpOpen: false }, () => {
      if (this.helpTrigger.current) {
        this.helpTrigger.current.focus();
      }
    });
  };

  dismissHelp = () => {
    this.setState({ helpOpen: false });
  };

  onSubmit = (event) => {
    event.preventDefault();
    if (this.props.busy) {
      return;
    }
    const { clientId, secret, label } = this.state;
    if (!clientId || !secret) {
      return;
    }
    if (looksLikeEmail(clientId)) {
      return;
    }
    this.props.onSubmit({ clientId, secret, label });
  };

  describedBy(id, extra) {
    const parts = [id + '-help'];
    if (extra) {
      parts.push(extra);
    }
    return parts.join(' ');
  }

  renderField({ id, label, value, onChange, type, placeholder, help, error, autoComplete, screenSize }) {
    const describedBy = this.describedBy(id, error ? id + '-error' : null);
    const isSecret = id === 'scania-connection-client-secret';
    return (
      <div style={getStyle(screenSize, styles, 'field')}>
        <label htmlFor={id} style={getStyle(screenSize, styles, 'label')}>{label}</label>
        <div style={isSecret ? getStyle(screenSize, styles, 'secretRow') : undefined}>
          <input
            id={id}
            type={type || 'text'}
            value={value}
            placeholder={placeholder}
            autoComplete={autoComplete || 'off'}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={describedBy}
            onChange={(event) => onChange(event.target.value)}
            style={getStyle(screenSize, styles, isSecret ? 'secretInput' : 'input')}
          />
          {isSecret && (
            <button
              type="button"
              aria-label={this.state.showSecret ? this.props.t('hideSecret') : this.props.t('showSecret')}
              aria-pressed={this.state.showSecret ? 'true' : 'false'}
              onClick={() => this.setState((prev) => ({ showSecret: !prev.showSecret }))}
              style={getStyle(screenSize, styles, 'secretToggle')}
            >
              <span
                aria-hidden="true"
                className={this.state.showSecret ? 'pi pi-eye-slash' : 'pi pi-eye'}
              />
            </button>
          )}
        </div>
        <span id={id + '-help'} style={getStyle(screenSize, styles, 'helpLine')}>{help}</span>
        {error && (
          <span id={id + '-error'} role="alert" style={getStyle(screenSize, styles, 'error')}>{error}</span>
        )}
      </div>
    );
  }

  renderDialog(screenSize) {
    const { t, busy, error, organizationName } = this.props;
    const { label, clientId, secret, showSecret, helpOpen } = this.state;
    const emailError = looksLikeEmail(clientId) ? t('clientIdLooksLikeEmail') : '';
    const canSubmit = !busy && !!clientId && !!secret && !emailError;
    const submitStyle = canSubmit
      ? getStyle(screenSize, styles, 'button')
      : { ...getStyle(screenSize, styles, 'button'), ...getStyle(screenSize, styles, 'buttonDisabled') };
    return (
      <div style={getStyle(screenSize, styles, 'dialog')}>
        <div style={getStyle(screenSize, styles, 'header')}>
          <h2 id="scania-connection-dialog-title" style={getStyle(screenSize, styles, 'title')}>
            {t('addCredentialTitle')}
          </h2>
          <button
            type="button"
            aria-label={t('closeDialog')}
            onClick={this.props.onClose}
            style={getStyle(screenSize, styles, 'closeIcon')}
          >
            ×
          </button>
          {organizationName ? (
            <p style={getStyle(screenSize, styles, 'orgLine')}>
              {t('organisationContext', { name: organizationName })}
            </p>
          ) : null}
        </div>
        <div data-scania-dialog-body="true" style={getStyle(screenSize, styles, 'body')}>
          <div style={getStyle(screenSize, styles, 'notice')}>
            <p style={getStyle(screenSize, styles, 'noticeTitle')}>{t('dialogNotLoginTitle')}</p>
            <p style={getStyle(screenSize, styles, 'noticeBody')}>{t('dialogNotLoginBody')}</p>
          </div>
          <div style={getStyle(screenSize, styles, 'helpWrap')}>
            <button
              type="button"
              ref={this.helpTrigger}
              aria-expanded={helpOpen ? 'true' : 'false'}
              aria-controls={HELP_ID}
              aria-haspopup="dialog"
              onClick={() => this.setState((prev) => ({ helpOpen: !prev.helpOpen }))}
              style={getStyle(screenSize, styles, 'helpTrigger')}
            >
              {t('helpTrigger')}
            </button>
            <ScaniaApiCredentialsHelp
              open={helpOpen}
              t={t}
              screenSize={screenSize}
              triggerEl={this.helpTrigger.current}
              onClose={this.closeHelp}
              onDismiss={this.dismissHelp}
            />
          </div>
          <form onSubmit={this.onSubmit}>
            {this.renderField({
              id: 'scania-connection-label',
              label: t('fieldName'),
              value: label,
              placeholder: t('fieldNamePlaceholder'),
              help: t('fieldNameHelp'),
              screenSize,
              onChange: (value) => this.setState({ label: value }),
            })}
            {this.renderField({
              id: 'scania-connection-client-id',
              label: t('fieldClientId'),
              value: clientId,
              placeholder: t('fieldClientIdPlaceholder'),
              help: t('fieldClientIdHelp'),
              error: emailError,
              screenSize,
              onChange: (value) => this.setState({ clientId: value }),
            })}
            {this.renderField({
              id: 'scania-connection-client-secret',
              label: t('fieldClientSecret'),
              value: secret,
              type: showSecret ? 'text' : 'password',
              placeholder: t('fieldClientSecretPlaceholder'),
              help: t('fieldClientSecretHelp'),
              autoComplete: 'new-password',
              screenSize,
              onChange: (value) => this.setState({ secret: value }),
            })}
            <p style={getStyle(screenSize, styles, 'grantNote')}>{t('dialogVehicleGrantNote')}</p>
            {error ? <p role="alert" style={getStyle(screenSize, styles, 'error')}>{error}</p> : null}
            <div style={getStyle(screenSize, styles, 'actions')}>
              <button
                type="button"
                onClick={this.props.onClose}
                style={getStyle(screenSize, styles, 'buttonMuted')}
              >
                {/* Busy uses Close because this only dismisses the form; the in-flight POST is not undone. */}
                {t(busy ? 'closeDialog' : 'cancel')}
              </button>
              <button type="submit" disabled={!canSubmit} style={submitStyle}>
                {busy ? t('saving') : t('submitAdd')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  render() {
    const { isOpen } = this.props;
    return (
      <CBMediaQuery>
        {(screenSize) => (
          <Modal
            isOpen={isOpen}
            onRequestClose={this.props.onClose}
            contentLabel={this.props.t('addCredentialTitle')}
            aria={{ labelledby: 'scania-connection-dialog-title', modal: true }}
            shouldCloseOnEsc={!this.state.helpOpen}
            shouldCloseOnOverlayClick={false}
            shouldReturnFocusAfterClose
            style={getModalStyle(screenSize, modalStyle)}
          >
            {this.renderDialog(screenSize)}
          </Modal>
        )}
      </CBMediaQuery>
    );
  }
}

export default ScaniaConnectionDialog;
export { looksLikeEmail };
